/*
 * Brisa drawer axis-preserve
 *
 * When a PDP variant picker exposes "Color" / "Acabado" and the user opens the
 * "Tambien de la familia" drawer, rewrite each entry link so the target product
 * lands on the same color (when that color exists at the target).
 *
 * Behaviour:
 *   1. Read the current PDP's selected Color/Acabado from the variant picker
 *      (radio:checked / select). Fallback: parse ?variant= and look it up via
 *      /products/<currentHandle>.js.
 *   2. For each non-current drawer link (.brisa-family-drawer__item with href
 *      matching /products/<handle>) fetch the target /products/<handle>.js (cached).
 *   3. If the target product has the same option name AND a variant with the same
 *      color value, rewrite ?variant=<vid>. Variant matched preserves any other
 *      query params already in the href (e.g. ?variant= injected by Liquid for
 *      size_matched entries — color wins because it's the axis the user picked).
 *   4. If no match (target lacks Color, or target lacks this color), leave the
 *      original link untouched (no break).
 *
 * Activation:
 *   - IntersectionObserver on the drawer surface — fetch+rewrite only when visible.
 *   - MutationObserver on variant picker change — re-run when current color changes
 *     after the drawer was already prepared.
 *
 * Safe by design: never throws; rewrites only when match is confident.
 */
(function () {
  'use strict';

  var COLOR_OPTION_NAMES = ['Color', 'Acabado', 'Colour'];
  var DATA_CACHE = Object.create(null);
  var PROCESSED = new WeakSet();

  function debug() {
    if (window.__BRISA_DRAWER_AXIS_DEBUG) {
      try { console.log.apply(console, ['[brisa-axis]'].concat([].slice.call(arguments))); } catch (e) {}
    }
  }

  function fetchProductData(handle) {
    if (!handle) return Promise.resolve(null);
    if (DATA_CACHE[handle]) return DATA_CACHE[handle];
    DATA_CACHE[handle] = fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    return DATA_CACHE[handle];
  }

  function getCurrentHandle() {
    var m = (location.pathname.match(/\/products\/([^\/?#]+)/) || [])[1];
    return m || null;
  }

  function getHandleFromHref(href) {
    if (!href) return null;
    var m = href.match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : null;
  }

  function getCurrentColor(picker) {
    if (!picker) picker = document.querySelector('variant-picker');
    if (!picker) return null;
    // Concept theme: radios use name pattern "Color-template--..." with checked state
    var radios = picker.querySelectorAll('input[type="radio"]:checked');
    for (var i = 0; i < radios.length; i++) {
      var r = radios[i];
      var name = (r.getAttribute('name') || '').toLowerCase().split('-template')[0];
      if (name.indexOf('color') !== -1 || name.indexOf('colour') !== -1 || name.indexOf('acabado') !== -1) {
        return r.getAttribute('data-option-value') || r.value || null;
      }
    }
    var selects = picker.querySelectorAll('select');
    for (var j = 0; j < selects.length; j++) {
      var s = selects[j];
      var sname = (s.getAttribute('name') || '').toLowerCase();
      if (sname.indexOf('color') !== -1 || sname.indexOf('colour') !== -1 || sname.indexOf('acabado') !== -1) {
        return s.value || null;
      }
    }
    return null;
  }

  function findColorOption(productData) {
    if (!productData || !Array.isArray(productData.options)) return -1;
    for (var i = 0; i < productData.options.length; i++) {
      var name = typeof productData.options[i] === 'string'
        ? productData.options[i]
        : (productData.options[i] && productData.options[i].name);
      if (!name) continue;
      if (COLOR_OPTION_NAMES.indexOf(name) !== -1) return i;
    }
    return -1;
  }

  function findVariantByColor(productData, color) {
    var idx = findColorOption(productData);
    if (idx < 0 || !color) return null;
    var variants = productData.variants || [];
    var availableMatch = null;
    var anyMatch = null;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var opt = v.options && v.options[idx];
      if (opt && opt === color) {
        if (v.available && !availableMatch) availableMatch = v;
        if (!anyMatch) anyMatch = v;
      }
    }
    return availableMatch || anyMatch;
  }

  function rewriteHref(link, newVariantId) {
    var current = link.getAttribute('href') || '';
    var base = current.split('?')[0];
    // Preserve all params except `variant`, then set variant.
    var qs = current.indexOf('?') >= 0 ? current.slice(current.indexOf('?') + 1) : '';
    var params = new URLSearchParams(qs);
    params.set('variant', String(newVariantId));
    var next = base + '?' + params.toString();
    if (next !== current) {
      if (!link.hasAttribute('data-brisa-axis-original')) {
        link.setAttribute('data-brisa-axis-original', current);
      }
      link.setAttribute('href', next);
      link.setAttribute('data-brisa-axis-applied', 'color:' + newVariantId);
    }
  }

  function restoreHref(link) {
    var original = link.getAttribute('data-brisa-axis-original');
    if (original) {
      link.setAttribute('href', original);
      link.removeAttribute('data-brisa-axis-applied');
    }
  }

  function processDrawer(drawer, currentColor) {
    if (!drawer) return;
    var links = drawer.querySelectorAll('a.brisa-family-drawer__item');
    if (!links.length) return;
    var currentHandle = getCurrentHandle();
    links.forEach(function (link) {
      if (link.classList.contains('brisa-family-drawer__item--current')) return;
      var targetHandle = getHandleFromHref(link.getAttribute('href'));
      if (!targetHandle || targetHandle === currentHandle) return;
      if (!currentColor) {
        // No color axis on the parent PDP — make sure we don't leave a stale rewrite.
        restoreHref(link);
        return;
      }
      fetchProductData(targetHandle).then(function (data) {
        if (!data) return;
        var match = findVariantByColor(data, currentColor);
        if (match) {
          rewriteHref(link, match.id);
          debug('rewrote', targetHandle, '→ variant', match.id, '(color=' + currentColor + ')');
        } else {
          restoreHref(link);
          debug('no color match for', targetHandle, '(color=' + currentColor + ')');
        }
      });
    });
  }

  function processAllDrawers() {
    var picker = document.querySelector('variant-picker');
    var color = getCurrentColor(picker);
    document.querySelectorAll('.brisa-family-drawer').forEach(function (d) {
      processDrawer(d, color);
    });
  }

  function observeDrawerVisibility(drawer) {
    if (!drawer || PROCESSED.has(drawer)) return;
    PROCESSED.add(drawer);
    // The drawer has `hidden` attribute when closed. Listen for that attr change
    // (cheaper than IntersectionObserver against an off-screen element).
    var mo = new MutationObserver(function () {
      if (!drawer.hasAttribute('hidden')) {
        processDrawer(drawer, getCurrentColor());
      }
    });
    mo.observe(drawer, { attributes: true, attributeFilter: ['hidden'] });
    // First pass in case it's already open / inline.
    if (!drawer.hasAttribute('hidden')) {
      processDrawer(drawer, getCurrentColor());
    }
  }

  function bootstrap() {
    document.querySelectorAll('.brisa-family-drawer').forEach(observeDrawerVisibility);

    // Re-run on variant change so the drawer reflects the latest color even if
    // the user changed it after opening once.
    document.addEventListener('variant:change', function () {
      requestAnimationFrame(processAllDrawers);
    });
    document.addEventListener('change', function (event) {
      var target = event.target;
      if (!target || !target.name) return;
      var name = target.name.toLowerCase().split('-template')[0];
      if (name.indexOf('color') === -1 && name.indexOf('colour') === -1 && name.indexOf('acabado') === -1) return;
      requestAnimationFrame(processAllDrawers);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
